import { useApolloClient } from '@apollo/client';
import { message, Tree, Card } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type EventApplicationModuleSpecsFragment } from '../../../../api/fragments/EventApplicationModuleSpecs';
import {
    UpdateEventApplicationVehicleAssignmentsDocument,
    type UpdateEventApplicationVehicleAssignmentsMutation,
    type UpdateEventApplicationVehicleAssignmentsMutationVariables,
} from '../../../../api/mutations/updateEventApplicationVehicleAssignments';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import DealerSelectorEmpty from '../shared/DealerSelectorEmpty';
import DealerVehicleSections from '../shared/DealerVehicleSections';
import { EditMode } from './type';
import VehicleAssignmentsEmpty from './VehicleAssignmentsEmpty';

export type UpdateVehicleAssignmentsValue = {
    dealerVehicles: Record<string, string[]>;
};

export type VehicleAssignmentsProps = {
    module: EventApplicationModuleSpecsFragment;
    editMode: EditMode;
    setEditMode: (value: EditMode) => void;
};

const VehicleAssignments = ({ module, editMode, setEditMode }: VehicleAssignmentsProps) => {
    const { t } = useTranslation(['eventApplicationModuleDetails']);
    const apolloClient = useApolloClient();
    const translatedString = useTranslatedString();

    const { dealerIds } = useMultipleDealerIds();

    const treeData = useMemo(
        () =>
            module.dealerVehicles
                .filter(({ dealerId }) => dealerIds.includes(dealerId))
                .map(({ dealer, vehicles }) => ({
                    key: dealer.id,
                    title: translatedString(dealer.legalName),
                    children: vehicles.map(vehicle => ({
                        key: `${dealer.id}-${vehicle.id}`,
                        title: translatedString(vehicle.name),
                    })),
                })),
        [module, translatedString, dealerIds]
    );

    const initialValues: UpdateVehicleAssignmentsValue = useMemo(
        () => ({
            dealerVehicles: Object.fromEntries(
                module.dealerVehicles.map(({ dealerId, vehicleSuiteIds }) => [dealerId, vehicleSuiteIds])
            ),
        }),
        [module.dealerVehicles]
    );

    const onSubmit = useHandleError<UpdateVehicleAssignmentsValue>(
        async values => {
            const { dealerVehicles } = values;

            const dealerVehiclesPayload = Object.entries(dealerVehicles)
                .map(([dealerId, vehicleSuiteIds]) => ({
                    dealerId,
                    vehicleSuiteIds,
                }))
                .filter(({ vehicleSuiteIds }) => vehicleSuiteIds?.length > 0);

            // submitting message
            message.loading({
                content: t('eventApplicationModuleDetails:messages.updateVehicleAssignmentsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<
                    UpdateEventApplicationVehicleAssignmentsMutation,
                    UpdateEventApplicationVehicleAssignmentsMutationVariables
                >({
                    mutation: UpdateEventApplicationVehicleAssignmentsDocument,
                    variables: {
                        moduleId: module.id,
                        dealerVehicles: dealerVehiclesPayload,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('eventApplicationModuleDetails:messages.updateVehicleAssignmentsSuccessful'),
                key: 'primary',
            });

            setEditMode(null);
        },
        [apolloClient, module.id, setEditMode, t]
    );

    if (editMode === EditMode.Vehicles) {
        return (
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form id="updateVehicleAssignments" name="updateVehicleAssignments" onSubmitCapture={handleSubmit}>
                        <DealerVehicleSections
                            module={module}
                            titles={[
                                t('eventApplicationModuleDetails:subTitles.dealerVehicles'),
                                t('eventApplicationModuleDetails:subTitles.selecedDealerVehicles'),
                            ]}
                        />
                    </Form>
                )}
            </Formik>
        );
    }

    if (dealerIds.length === 0) {
        return <DealerSelectorEmpty />;
    }

    if (treeData.length === 0) {
        return <VehicleAssignmentsEmpty setEditMode={setEditMode} />;
    }

    return (
        <Card>
            <Tree selectable={false} treeData={treeData} defaultExpandAll />
        </Card>
    );
};

export default VehicleAssignments;
